import { Trans } from '@lingui/react/macro';
import type {
  FacilityTransferDirection,
  FacilityTransferItemPublicRecord,
  FacilityTransferRoundPublicRecord
} from '@navio/nsdk';
import { useFacilityTimeZone } from './facilityTimeZone';
import { groupItemsByRound, mergeRoundsByTime, type PickupRoundGroup } from './pickupRounds';
import { WheelSetItemCard } from './WheelSetItemCard';

interface Props {
  items: FacilityTransferItemPublicRecord[];
  rounds: FacilityTransferRoundPublicRecord[];
  direction: FacilityTransferDirection;
}

export const PickupRoundGroups = ({ items, rounds, direction }: Props) => {
  const timeZone = useFacilityTimeZone();
  const cards = (groupItems: FacilityTransferItemPublicRecord[]) => (
    <ul className='flex flex-col gap-2'>
      {groupItems.map((item) => (
        <WheelSetItemCard key={item.id} item={item} direction={direction} />
      ))}
    </ul>
  );

  // One stop needs no order to follow, so the rounds only show once the vehicle comes more than once.
  if (mergeRoundsByTime(rounds, direction, timeZone).length <= 1) {
    return cards(items);
  }

  const groups: PickupRoundGroup[] = groupItemsByRound(items, rounds, direction, timeZone);

  return (
    <div className='flex flex-col gap-5'>
      {groups.map((group) => {
        const count = group.items.length;
        return (
          <div key={group.key} className='flex flex-col gap-2'>
            <div className='flex items-center gap-3 rounded-lg bg-primary px-3 py-2.5 text-white shadow-sm'>
              <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-lg font-bold text-primary'>
                {group.marker}
              </span>
              <div className='flex min-w-0 flex-col'>
                <h3 className='text-base font-semibold'>{group.title}</h3>
                {group.timeLabel && <p className='text-sm text-white/85'>{group.timeLabel}</p>}
              </div>
              <span className='ml-auto shrink-0 text-sm text-white/85'>
                <Trans>{count} wheel sets</Trans>
              </span>
            </div>
            {cards(group.items)}
          </div>
        );
      })}
    </div>
  );
};
